import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import Card from "../../../common/components/Card";
import DataTable, { customCtrlTypeSupported } from "../../../common/components/DataTable";
import DateInput from "../../../common/components/DateInput";
import { DefaultButton } from "../../../common/components/DefaultButton";
import { FloatingLabel } from "../../../common/components/FloatingLabel";
import { Col, Container, Row } from "../../../common/components/Grid";
import TimeInput from "../../../common/components/TimeInput";
import useClientConfig from "../../../common/hooks/clientConfigHook";
import { cdate, now } from "../../../common/utils/CDate";
import { dynamicSort } from "../../../common/utils/tableUtilities";
import { useEpcrTreatmentMedicationDefs } from "../../../reducers/epcrTreatmentMedicationSlice";
import { calculateDose, getTreatmentRoutes } from "../../../utils/dosingUtilities";
import { useEpcrEditorEpcr, useEpcrEditorIntent, useEpcrEditorTreatmentMeds } from '../EpcrContext';
import ReactForm from "../../../common/components/ReactForm";
import { deepClone, deepMerge } from "../../../common/utils/objectUtilities";
import CustomForm from "../../../common/components/CustomForm";
import PropTypes from "prop-types";
import DataList from "../../../common/components/DataList";

let genTimestamp = (tz) => {
    let timeNow = now()
    timeNow.seconds(0);
    timeNow.milliseconds(0);
    return timeNow.toISOString();
}

const isPaediatric = (minAdultAge, a) => {
    return (minAdultAge && a) ? a < minAdultAge : false;
};

const EpcrTreatmentMedications = ({ disabled = false }) => {
    const { t } = useTranslation();
    const clientConfig = useClientConfig();
    const { epcr } = useEpcrEditorEpcr();
    const medDefs = useEpcrTreatmentMedicationDefs();
    const { treatmentMeds, addTreatmentMed, removeTreatmentMed } = useEpcrEditorTreatmentMeds();
    const newMedRef = useRef();

    const [newMed, setNewMed] = useState({timestamp: genTimestamp(clientConfig.tz)});
    
    const age = useMemo(() => epcr?.dob ? now().diff(cdate(epcr.dob), 'year') : undefined, [epcr?.dob]);
    const paediatric = useMemo(() => isPaediatric(clientConfig.minAdultAge, age), [clientConfig.minAdultAge, age])

    let displayMedDefs = useMemo(() => {
        let defs = deepClone(medDefs);
        defs = defs.filter(d => (!paediatric && d.dosing?.adult) || (!d.dosing?.paediatric && !d.dosing?.adult) || (paediatric && d.dosing?.paediatric));
        defs.sort(dynamicSort(['name']));
        return defs;
    }, [paediatric, medDefs]) 


    const currentMed = useMemo(() => displayMedDefs.find(m => m.id === newMed.medicationid), [displayMedDefs, newMed.medicationid]);
    const routes = useMemo(() => getTreatmentRoutes(paediatric, currentMed?.dosing), [paediatric, currentMed?.dosing]);
    const doseData = useMemo(() => calculateDose(paediatric, age, epcr?.weight, newMed.route, currentMed), [paediatric, age, epcr?.weight, newMed.route, currentMed])
    // #endregion

    // #region watches for changes in weight and age
    useEffect(() => {
        if (doseData?.amount) {
            setNewMed(newMed => ({ ...newMed, dose: doseData.amount }));
        } else {
            setNewMed(newMed => {
                let m = deepClone(newMed);
                delete m.dose;
                return m;
            })
        }
    }, [doseData?.amount]);

    useEffect(() => {
        if (doseData?.units) {
            setNewMed(newMed => ({ ...newMed, units: doseData.units }));
        } else {
            setNewMed(newMed => {
                let m = deepClone(newMed);
                delete m.units;
                return m;
            })
        }
    }, [doseData?.units])

    useEffect(() => {
        setNewMed(newMed => {
            if (routes.every(r => r !== newMed.route)) {
                let m = deepClone(newMed);
                if (routes.length === 2 && routes.some(r => r === 'Discard')) {
                    m.route = routes.find(r => r !== 'Discard');
                } else {
                    delete m.route;
                }
                return m;
            }
            return newMed;
        })
    }, [routes]);

    useEffect(() => {
        if (!currentMed) {
            setNewMed(newMed => {
                const m = deepClone(newMed);
                delete m.medicationid;
                delete m.route;
                delete m.dose;
                return m;
            })
        }
    }, [currentMed]);

    let addMedication = () => {
        if (newMedRef.current?.checkValidity()) {
            addTreatmentMed({ ...newMed });
            if (clientConfig.epcrTimestampDefault) {
                setNewMed({ timestamp: genTimestamp(clientConfig.tz) });
            } else {
                setNewMed({});
            }
        }
    }
    
    let removeMedication = (med) => {
        removeTreatmentMed(med);
    }

    const args = useMemo(() => ({ 
        medicationid: { paediatric }, 
        route: { paediatric, dosing: currentMed?.dosing },
        dose: { paediatric, doseData }
    }), [paediatric, currentMed?.dosing, doseData]);

    const rowDef = useMemo(() => [
        { borderless: true, rows: [
            [
                { type: 'date', field: 'timestamp', label: t('date'), colspan: 3 },
                { type: 'time', field: 'timestamp', label: t('time'), colspan: 3 }
            ]
        ]},
        ...(clientConfig.epcrTreatmentMedicationFormDef || [])
    ], [t, clientConfig.epcrTreatmentMedicationFormDef]);
    
    return <Card title={t('medication', {count: 100})}>
            {!disabled && <ReactForm ref={newMedRef}>
                {newMed.timestamp ? 
                <Row>
                    <Col md>
                        <Row>
                            <Col md>
                                <TimeInput value={newMed.timestamp} onChange={t => setNewMed({ ...newMed, timestamp: t })} placeholder={t('timeOfDay')}></TimeInput>
                            </Col>
                            <Col md>
                                <DateInput value={newMed.timestamp} onChange={t => setNewMed({ ...newMed, timestamp: t })} placeholder={t('dateTime')}></DateInput>
                            </Col>
                        </Row>
                    </Col>
                    <Col className="text-end" md="auto">
                        <FloatingLabel>
                            <Button variant='link' onClick={_ => setNewMed({ ...newMed, timestamp: undefined })}>{t('clearTimestamp')}</Button>
                        </FloatingLabel>
                    </Col>
                </Row>
                :
                <Row>
                    <Col md></Col>
                    <Col className="text-end" md="auto"><Button variant='link' onClick={_ => setNewMed({...newMed, timestamp: genTimestamp()})}>{t('overrideTimestamp')}</Button></Col>
                </Row>
                }
                <CustomForm def={clientConfig.epcrTreatmentMedicationFormDef} data={newMed} args={args} onChange={update => setNewMed(newMed => deepMerge({}, newMed, update))} required></CustomForm>
            </ReactForm> }
        <Row>
            <Col></Col>
            <Col className="text-end" md="auto"><FloatingLabel placeholder=" "><DefaultButton kind='add' onClick={addMedication}></DefaultButton></FloatingLabel></Col>
        </Row>
            <DataList rowDef={rowDef} readonly defaultSort={['-timestamp']} value={treatmentMeds ?? null} onRemove={removeMedication} emptyMessage={t('noMedications')}></DataList>
        </Card>
}

const EpcrMedication = ({ disabled = false }) => {
    const { epcr, update } = useEpcrEditorEpcr();
    const intent = useEpcrEditorIntent();
    const clientConfig = useClientConfig();

    const runPCRUpdate = useCallback((field, value) => {
        update(field, value);
    }, [update]);

    const def = useMemo(() => {
        if (intent === 'search') {
            let def = deepClone(clientConfig.epcrTreatmentFormSpec);
            def.forEach(section => {
                section.rows = section.rows.map(row => row.filter(cell => customCtrlTypeSupported(cell)));
                section.rows = section.rows.filter(row => row.length > 0);
            });
            def = def.filter(section => section.rows.length > 0);
            return def;
        }
        return clientConfig.epcrTreatmentFormSpec;
    }, [intent, clientConfig.epcrTreatmentFormSpec]);

    return <Container fluid>
        <EpcrTreatmentMedications disabled={disabled}></EpcrTreatmentMedications>
        { clientConfig.epcrMedicationFormSpec && <CustomForm disabled={disabled} data={epcr?.customfields} def={def} hideLabel={true} onChange={c => runPCRUpdate('customfields', c)}></CustomForm> }
    </Container>
}
EpcrMedication.propTypes = {
    disabled: PropTypes.bool
}

export default EpcrMedication;